import type { RequestState } from '@qcdoc/shared/redux/requests.ts';
import { requestKey } from '@qcdoc/shared/redux/requests.ts';
import type { vendorService } from '@qcdoc/shared/services/repositories/vendorService.ts';
import { createSlice } from '@reduxjs/toolkit';
import { fetchVendorActiveVqcas,fetchVendorPendingVqcas,fetchVendorVqcaDocument,fetchVendorVqcas,fetchVendorVqcaSpecs,mutateSignVendorVqca } from '../thunks/vendorVqcaThunks.ts';

export interface VendorVqcaState {
  revision: number;
  mutations: Record<string, Record<string, RequestState<void>>>;
  lists: Record<string, RequestState<Awaited<ReturnType<typeof vendorService.getVqcaList>>>>;
  pending: Record<string, RequestState<Awaited<ReturnType<typeof vendorService.getVqcaPending>>>>;
  active: Record<string, RequestState<Awaited<ReturnType<typeof vendorService.getActiveVqcas>>>>;
  specs: Record<string, RequestState<Awaited<ReturnType<typeof vendorService.getVqcaSpecs>>>>;
  documents: Record<string, RequestState<Awaited<ReturnType<typeof vendorService.getVqcaDocument>>>>;
}

export const initialVendorVqcaState: VendorVqcaState = { revision: 0, mutations: {}, lists: {}, pending: {}, active: {}, specs: {}, documents: {} };

const slice = createSlice({
  name: 'vendorVqca',
  initialState: initialVendorVqcaState,
  reducers: { resetVendorVqcaState: () => initialVendorVqcaState },
  extraReducers: builder => {
    builder.addCase(fetchVendorVqcas.pending, (state, action) => {
      const previous = state.lists[requestKey(action.meta.arg ?? null)];
      state.lists[requestKey(action.meta.arg ?? null)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(fetchVendorVqcas.fulfilled, (state, action) => {
      const request = state.lists[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId || request.revision !== state.revision) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
      request.data = action.payload;
    });
    builder.addCase(fetchVendorVqcas.rejected, (state, action) => {
      const request = state.lists[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addCase(fetchVendorPendingVqcas.pending, (state, action) => {
      const previous = state.pending[requestKey(action.meta.arg ?? null)];
      state.pending[requestKey(action.meta.arg ?? null)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(fetchVendorPendingVqcas.fulfilled, (state, action) => {
      const request = state.pending[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId || request.revision !== state.revision) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
      request.data = action.payload;
    });
    builder.addCase(fetchVendorPendingVqcas.rejected, (state, action) => {
      const request = state.pending[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addCase(fetchVendorActiveVqcas.pending, (state, action) => {
      const previous = state.active[requestKey(action.meta.arg ?? null)];
      state.active[requestKey(action.meta.arg ?? null)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(fetchVendorActiveVqcas.fulfilled, (state, action) => {
      const request = state.active[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId || request.revision !== state.revision) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
      request.data = action.payload;
    });
    builder.addCase(fetchVendorActiveVqcas.rejected, (state, action) => {
      const request = state.active[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addCase(fetchVendorVqcaSpecs.pending, (state, action) => {
      const previous = state.specs[requestKey(action.meta.arg ?? null)];
      state.specs[requestKey(action.meta.arg ?? null)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(fetchVendorVqcaSpecs.fulfilled, (state, action) => {
      const request = state.specs[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId || request.revision !== state.revision) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
      request.data = action.payload;
    });
    builder.addCase(fetchVendorVqcaSpecs.rejected, (state, action) => {
      const request = state.specs[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addCase(fetchVendorVqcaDocument.pending, (state, action) => {
      const previous = state.documents[requestKey(action.meta.arg ?? null)];
      state.documents[requestKey(action.meta.arg ?? null)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(fetchVendorVqcaDocument.fulfilled, (state, action) => {
      const request = state.documents[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId || request.revision !== state.revision) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
      request.data = action.payload;
    });
    builder.addCase(fetchVendorVqcaDocument.rejected, (state, action) => {
      const request = state.documents[requestKey(action.meta.arg ?? null)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addCase(mutateSignVendorVqca.pending, (state, action) => {
      state.mutations.signVendorVqca ??= {};
      const previous = state.mutations.signVendorVqca[requestKey(action.meta.arg.id)];
      state.mutations.signVendorVqca[requestKey(action.meta.arg.id)] = { ...previous, status: 'pending', requestId: action.meta.requestId, error: undefined, updatedAt: 0, revision: state.revision };
    });
    builder.addCase(mutateSignVendorVqca.fulfilled, (state, action) => {
      const request = state.mutations.signVendorVqca?.[requestKey(action.meta.arg.id)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = 'fulfilled';
      request.requestId = undefined;
      request.error = undefined;
    });
    builder.addCase(mutateSignVendorVqca.rejected, (state, action) => {
      const request = state.mutations.signVendorVqca?.[requestKey(action.meta.arg.id)];
      if (request?.requestId !== action.meta.requestId) return;
      request.status = action.meta.aborted ? 'idle' : 'rejected';
      request.requestId = undefined;
      request.error = action.meta.aborted ? undefined : { message: action.payload ?? action.error.message ?? 'VQCA request failed.' };
    });
    builder.addMatcher(action => action.type.startsWith('vendorVqca/mutate') && action.type.endsWith('/fulfilled'), state => { state.revision += 1; });
  },
});

export const { resetVendorVqcaState } = slice.actions;
export default slice.reducer;
